import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import _ from 'lodash';
import { JOB_STATUS, JOB_TYPE } from '../../../@core/models/enums';
import { IUser } from '../../../@core/models/model';
import { AuthenticationService } from '../../../@core/services/authentication.service';
import { ToastService } from '../../../@core/services/toast.service';
import { IJob, IJobGroup, JOB_GROUP, JobFilters, JobService } from '../../../contract-management/services/job.service';
import { JOB_FILTER_STATUS } from '../notifications/notifications.component';
import { DateFormatterPipe } from '../../pipes/dateFormater.pipe';
import { SearchBarComponent } from '../search-bar/search-bar.component';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { IconComponent } from '../../../@core/components/ui/icon/icon.component';
import { PopoverComponent } from '../../../@core/components/ui/popover/popover.component';
import { TextCasePipe } from '../../pipes/textCase.pipe';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { FullNamePipe } from "../../pipes/fullName.pipe";
import { Drawer } from "primeng/drawer";
import { SelectComponent } from "src/app/@core/components/ui/select";
import type { SelectChangeEvent, SelectConfig } from "src/app/@core/components/ui/select/select.model";
import { DatepickerComponent } from "src/app/@core/components/ui/datepicker";
import { PaginatorModule } from 'primeng/paginator';
import { AccordionModule } from 'primeng/accordion';

@Component({
  selector: 'app-tasks',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SearchBarComponent,
    ButtonComponent,
    IconComponent,
    PopoverComponent,
    TextCasePipe,
    TooltipModule,
    FullNamePipe,
    Drawer,
    SelectComponent,
    DatepickerComponent,
    PaginatorModule,
    AccordionModule,
    DateFormatterPipe
  ],
  templateUrl: './tasks.component.html',
  styleUrl: './tasks.component.scss',
})
export class TasksComponent implements OnInit {
  @Input() projectId?: string;

  jobGroups: IJobGroup[] = [];
  jobGroupsTotal = 0;
  jobFilters: { selected: JOB_FILTER_STATUS; skip: number; limit: number } = {
    selected: JOB_FILTER_STATUS.ALL,
    skip: 0,
    limit: 20,
  };
  jobLoading = false;
  searchValue = '';
  currentUser!: IUser;
  paginationFirst = 0;
  rowsPerPage = 20;

  JOB_FILTER_STATUS = JOB_FILTER_STATUS;
  JOB_STATUS = JOB_STATUS;
  JOB_GROUP = JOB_GROUP;

  filterPanelVisible = false;

  selectedJobType: JOB_TYPE | null = null;
  jobTypeOptions: { label: string; value: JOB_TYPE }[] = [
    { label: 'Document Generation', value: JOB_TYPE.GENERATE_DOCUMENT },
    { label: 'Formula Dashboard Generation', value: JOB_TYPE.GEOTECHNICAL_FORMULA_DASHBOARD },
    { label: 'Value Engineering', value: JOB_TYPE.GEOTECHNICAL_VALUE_ENGINEERING },
    { label: 'Geotechnical Summary', value: JOB_TYPE.GEOTECHNICAL_SUMMARY },
    { label: 'Geotechnical Analysis Summary', value: JOB_TYPE.GEOTECHNICAL_ANALYSIS_SUMMARY },
    { label: 'Analysis Excel Generation', value: JOB_TYPE.GENERATE_ANALYSIS_EXCEL },
    { label: 'Geotechnical Vendor Analysis', value: JOB_TYPE.GEOTECHNICAL_VENDOR_ANALYSIS },
  ];
  jobTypeSelectConfig: SelectConfig = {
    placeholder: 'Select job type',
    searchable: true,
    clearable: false,
    style: { 'width': '100%' }
  };

  selectedStatus: JOB_STATUS | null = null;
  statusOptions: { label: string; value: JOB_STATUS }[] = [
    { label: 'Started', value: JOB_STATUS.STARTED },
    { label: 'Completed', value: JOB_STATUS.COMPLETED },
    { label: 'Failed', value: JOB_STATUS.FAILED },
    { label: 'Cancelled', value: JOB_STATUS.CANCELLED },
  ];
  statusSelectConfig: SelectConfig = {
    placeholder: 'Select status',
    searchable: true,
    clearable: false,
    style: { 'width': '100%' }
  };

  filterDateRange: Date[] | null = null;
  datePickerConfig = {
    selectionMode: 'range' as const,
    showIcon: true,
    appendTo: 'body' as const,
    dateFormat: 'dd M yy',
    placeholder: 'Select Created Date',
  };

  get tasksTotalRecords(): number {
    return this.jobGroupsTotal;
  }

  constructor(
    private jobService: JobService,
    private authService: AuthenticationService,
    private router: Router,
    private toastService: ToastService
  ) {
    this.currentUser = this.authService.getUser() ?? ({} as IUser);
  }

  ngOnInit() {
    this.jobFilters.skip = 0;
    this.jobGroups = [];
    this.fetchJobsData(this.jobFilters);
  }

  fetchJobsData = _.debounce(
    (jobFilters: JobFilters) => {
      this.jobLoading = true;
      const filters: JobFilters = {
        selected: JOB_FILTER_STATUS.ALL,
        skip: jobFilters.skip,
        limit: jobFilters.limit,
        project: this.projectId,
        allowedJobs: [
          JOB_TYPE.GENERATE_DOCUMENT,
          JOB_TYPE.GEOTECHNICAL_FORMULA_DASHBOARD,
          JOB_TYPE.GEOTECHNICAL_VALUE_ENGINEERING,
          JOB_TYPE.GEOTECHNICAL_SUMMARY,
          JOB_TYPE.GEOTECHNICAL_ANALYSIS_SUMMARY,
          JOB_TYPE.GEOTECHNICAL_VENDOR_ANALYSIS,
          JOB_TYPE.GENERATE_ANALYSIS_EXCEL,
          JOB_TYPE.GEOTECHNICAL_ANALYSIS,
        ],
        ...(this.searchValue?.trim() && { search: this.searchValue.trim() }),
        ...(this.selectedJobType != null && { jobType: this.selectedJobType }),
        ...(this.selectedStatus != null && { status: this.selectedStatus }),
        ...(this.filterDateRange?.[0] && { dateFrom: this.filterDateRange[0].toISOString() }),
        ...(this.filterDateRange?.[1] && { dateTo: this.filterDateRange[1].toISOString() }),
      };
      this.jobService.getJobsGrouped(filters).subscribe({
        next: (res) => {
          if (res.success && res.data) {
            this.jobGroups = res.data.groups ?? [];
            this.jobGroupsTotal = res.data.total ?? 0;
          }
          this.jobLoading = false;
        },
        error: () => { this.jobLoading = false; }
      });
    },
    300
  );

  onSearch() {
    this.resetPagination();
    this.fetchJobsData(this.jobFilters);
  }

  onFilterStatus(status: JOB_FILTER_STATUS) {
    if (this.jobLoading) return;
    this.jobFilters.selected = status;
    this.resetPagination();
    this.fetchJobsData(this.jobFilters);
  }

  onPageChange(event: { first: number; rows: number }) {
    this.paginationFirst = event.first;
    if (event.rows != null) this.rowsPerPage = event.rows;
    this.jobFilters.skip = event.first;
    this.jobFilters.limit = event.rows ?? this.rowsPerPage;
    this.fetchJobsData(this.jobFilters);
  }

  private resetPagination() {
    this.jobFilters.skip = 0;
    this.paginationFirst = 0;
    this.jobGroups = [];
  }

  getJobTitle(job: IJob): string {
    switch (job.type) {
      case JOB_TYPE.REFRESH:
        return 'Generating Insights';
      case JOB_TYPE.TAG_EXTRACTION:
        return 'Tags Extraction';
      case JOB_TYPE.SINGLE_TAG_EXTRACTION:
        return 'Single Tag Extraction';
      case JOB_TYPE.CHECKLIST:
        return 'Checklist Compliance';
      case JOB_TYPE.SINGLE_CHECKLIST_ITEM:
        return 'Single Checklist Compliance';
      case JOB_TYPE.SUMMARY:
        return 'Summary Generation';
      case JOB_TYPE.REPO_TAG_EXTRACTION:
        return 'Repository Tags Extraction';
      case JOB_TYPE.GENERATE_DOCUMENT:
        return 'Document Generation';
      case JOB_TYPE.ATTACHMENT_EMBEDDINGS_GENERATION:
        return 'Attachment Embeddings Generation';
      case JOB_TYPE.GENERATE_TEMPLATE:
        return 'Template Generation';
      case JOB_TYPE.GENERATE_ANALYSIS_EXCEL:
        return 'Analysis Excel Generation';
      case JOB_TYPE.GEOTECHNICAL_ANALYSIS:
        return 'Geotechnical Analysis';
      case JOB_TYPE.GEOTECHNICAL_FORMULA_DASHBOARD:
        return 'Formula Dashboard Generation';
      case JOB_TYPE.GEOTECHNICAL_VALUE_ENGINEERING:
        return 'Value Engineering';
      case JOB_TYPE.GEOTECHNICAL_SUMMARY:
        return 'Geotechnical Summary';
      case JOB_TYPE.GEOTECHNICAL_ANALYSIS_SUMMARY:
        return 'Geotechnical Analysis Summary';
      case JOB_TYPE.GEOTECHNICAL_VENDOR_ANALYSIS:
        return 'Geotechnical Vendor Analysis';
      case JOB_TYPE.CLAUSE_EXTRACTION_RETRY:
        return 'Clause Extraction';
      default:
        return 'Task';
    }
  }

  getJobDisplayName(job: IJob): string {
    const name = typeof job.item === 'object' && job.item?.name ? job.item.name : '';
    return name || this.getJobTitle(job);
  }

  getStatusIcon(job: IJob): string {
    switch (job.status) {
      case JOB_STATUS.COMPLETED:
        return 'circle-check';
      case JOB_STATUS.FAILED:
        return 'circle-alert';
      case JOB_STATUS.STARTED:
        return 'loader-circle';
      default:
        return 'circle';
    }
  }

  getStatusIconVariant(job: IJob): string {
    switch (job.status) {
      case JOB_STATUS.COMPLETED:
        return 'success';
      case JOB_STATUS.FAILED:
        return 'danger';
      default:
        return 'muted';
    }
  }

  toggleFilterPanel() {
    this.filterPanelVisible = !this.filterPanelVisible;
  }

  handleChange(event: SelectChangeEvent) {
    this.selectedJobType = event.value ?? null;
  }

  handleStatusChange(event: SelectChangeEvent) {
    this.selectedStatus = event.value ?? null;
  }

  clearAllFilters() {
    this.selectedJobType = null;
    this.selectedStatus = null;
    this.filterDateRange = null;
    this.resetPagination();
    this.fetchJobsData(this.jobFilters);
    this.filterPanelVisible = false;
  }

  applyFilters() {
    this.resetPagination();
    this.fetchJobsData(this.jobFilters);
    this.filterPanelVisible = false;
  }

  allowCancel(job: IJob): boolean {
    return job.status === JOB_STATUS.STARTED;
  }

  onCancel(job: IJob, popover: PopoverComponent) {
    popover.hide();
    this.jobService.cancelJob(job._id).subscribe((res) => {
      if (res.success && res.data) {
        this.toastService.success('Task cancelled successfully.');
        this.onFilterStatus(this.jobFilters.selected);
      }
    });
  }

  navigateToGroup(event: Event, group: IJobGroup) {
    event.stopPropagation();
    if (group.groupType === JOB_GROUP.GEOTECHNICAL_ANALYSIS) {
      const url = this.router.serializeUrl(this.router.createUrlTree(['/geotechnical-analysis-report'], { queryParams: { analysisId: group.groupId.split(':')[1] } }));
      window.open(url, '_blank');
    } else if (group.groupType === JOB_GROUP.CONTRACT) {
      const url = this.router.serializeUrl(this.router.createUrlTree(['/edit-document'], { queryParams: { documentId: group.groupId.split(':')[1] } }));
      window.open(url, '_blank');
    }
  }

  jobAction(job: IJob) {
    if (job.type === JOB_TYPE.GENERATE_TEMPLATE) {
      const templateId = job.metadata?.templateId;
      if (templateId && job.status === JOB_STATUS.COMPLETED) {
        this.router.navigate(['/settings/library/template-library'], {
          queryParams: { templateId },
        });
      }
      return;
    }
    const contractId = job.metadata?.contractId;
    if (
      contractId &&
      (job.type === JOB_TYPE.GENERATE_DOCUMENT ? job.status === JOB_STATUS.COMPLETED : true)
    ) {
      this.router.navigate(['/edit-document'], { queryParams: { documentId: contractId } });
    }
  }

}
